import React from 'react';
import { FileText, Mail, CreditCard, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { PAYMENT_TERMS_OPTIONS } from '../clientsConstants';

const termsLabel = (value) => PAYMENT_TERMS_OPTIONS.find(o => o.value === value)?.label || 'Net 30';

export const TermsTab = ({ clientDetail, editMode, editForm, setEditForm, savingChanges, onSave, onCancelEdit }) => (
  <div className="space-y-4 mt-4">
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-500" />
          Payment Terms
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {editMode ? (
          <>
            <div>
              <Label>Payment Terms</Label>
              <Select
                value={editForm.payment_terms}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, payment_terms: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select terms" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TERMS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credit Limit ($)</Label>
              <Input
                type="number"
                placeholder="No limit"
                value={editForm.credit_limit}
                onChange={(e) => setEditForm(prev => ({ ...prev, credit_limit: e.target.value }))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for no limit</p>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Payment Terms</p>
              <p className="font-medium">{termsLabel(clientDetail.preferences?.payment_terms)}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Credit Limit</p>
              <p className="font-medium">
                {clientDetail.preferences?.credit_limit ? `$${clientDetail.preferences.credit_limit}` : 'No limit'}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-5 w-5 text-amber-500" />
          Invoice Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {editMode ? (
          <>
            <div>
              <Label>Invoice Email</Label>
              <Input
                type="email"
                placeholder="billing@company.com"
                value={editForm.invoice_email}
                onChange={(e) => setEditForm(prev => ({ ...prev, invoice_email: e.target.value }))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Separate from primary contact email</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_invoice"
                checked={editForm.auto_invoice}
                onChange={(e) => setEditForm(prev => ({ ...prev, auto_invoice: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="auto_invoice">Auto-generate invoices on delivery</Label>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
              <input
                type="checkbox"
                id="auto_pay_enabled"
                checked={editForm.auto_pay_enabled}
                onChange={(e) => setEditForm(prev => ({ ...prev, auto_pay_enabled: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <div>
                <Label htmlFor="auto_pay_enabled">Enable Auto-Pay</Label>
                <p className="text-xs text-gray-500">
                  Automatically charge this client based on their payment terms ({termsLabel(editForm.payment_terms)})
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Invoice Email</p>
              <p className="font-medium">{clientDetail.preferences?.invoice_email || 'Uses primary contact'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Auto Invoice</p>
              <p className="font-medium">
                {clientDetail.preferences?.auto_invoice !== false ? 'Enabled' : 'Disabled'}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Auto-Pay</p>
              <p className={`font-medium ${clientDetail.preferences?.auto_pay_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                {clientDetail.preferences?.auto_pay_enabled ? 'Enabled' : 'Disabled'}
              </p>
              {clientDetail.preferences?.auto_pay_enabled && (
                <p className="text-xs text-gray-500 mt-1">
                  Charges on {termsLabel(clientDetail.preferences?.payment_terms)}
                </p>
              )}
              {clientDetail.preferences?.auto_pay_enabled && clientDetail.has_payment_method === false && (
                <div className="mt-2 p-2 bg-yellow-50 rounded flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-700">No payment method on file. Auto-pay will fail.</p>
                </div>
              )}
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase">Payment Method</p>
              <div className="flex items-center gap-2 mt-1">
                <CreditCard className={`h-4 w-4 ${clientDetail.has_payment_method ? 'text-green-600' : 'text-gray-400'}`} />
                <p className={`font-medium ${clientDetail.has_payment_method ? 'text-green-600' : 'text-gray-500'}`}>
                  {clientDetail.has_payment_method ? 'Card on file' : 'Not configured'}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {editMode && (
      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" onClick={onCancelEdit}>Cancel</Button>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={onSave} disabled={savingChanges}>
          {savingChanges ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    )}
  </div>
);
